import { FormEvent, useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";

import { useAuthStore } from "@/auth/authStore";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  fetchMyConferenceContext,
  fetchMyPapers,
  fetchSessions,
  submitPaperRevision,
  type ConferencePaper,
  type ConferenceRegistration,
  type ConferenceSession,
} from "./api";
import { formatDateTime, paperStatusLabel, pickLocalized } from "./format";

function canSubmitRevision(paper: ConferencePaper): boolean {
  return paper.status === "minor_revision" || paper.status === "major_revision";
}

export function DashboardPage() {
  const { t, locale } = useLanguage();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const [reg, setReg] = useState<ConferenceRegistration | null>(null);
  const [reviewerOnly, setReviewerOnly] = useState<boolean | null>(null);
  const [papers, setPapers] = useState<ConferencePaper[]>([]);
  const [live, setLive] = useState<ConferenceSession | null>(null);
  const [nextSession, setNextSession] = useState<ConferenceSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviseId, setReviseId] = useState<string | null>(null);
  const [reviseNotes, setReviseNotes] = useState("");
  const [reviseFile, setReviseFile] = useState<File | null>(null);
  const [reviseBusy, setReviseBusy] = useState(false);
  const [reviseOk, setReviseOk] = useState<string | null>(null);

  const reloadPapers = () =>
    fetchMyPapers().then((p) => {
      setPapers(p);
    });

  useEffect(() => {
    Promise.all([fetchMyConferenceContext(), fetchMyPapers(), fetchSessions()])
      .then(([ctx, p, sessions]) => {
        setReviewerOnly(Boolean(ctx.is_reviewer && !ctx.has_registration));
        setReg(ctx.registration);
        setPapers(p);
        const liveNow = sessions.find((s) => s.status === "live") || null;
        setLive(liveNow);
        const upcoming = sessions
          .filter((s) => s.status === "scheduled")
          .sort((a, b) => +new Date(a.starts_at) - +new Date(b.starts_at))[0];
        setNextSession(upcoming || null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : t.common.error));
  }, [t.common.error]);

  useEffect(() => {
    if (location.hash === "#my-papers") {
      document.getElementById("my-papers")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash, papers.length]);

  const onSubmitRevision = async (e: FormEvent, paperId: string) => {
    e.preventDefault();
    if (!reviseFile) {
      setError(t.dashboard.revisionNeedFile);
      return;
    }
    setReviseBusy(true);
    setError(null);
    setReviseOk(null);
    try {
      const form = new FormData();
      form.append("file", reviseFile);
      if (reviseNotes.trim()) form.append("revision_notes", reviseNotes.trim());
      const result = await submitPaperRevision(paperId, form);
      setReviseOk(
        t.dashboard.revisionSubmitted.replace(
          "{n}",
          String(result.revision.revision_number),
        ),
      );
      setReviseId(null);
      setReviseFile(null);
      setReviseNotes("");
      await reloadPapers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setReviseBusy(false);
    }
  };

  if (reviewerOnly) {
    return <Navigate to="/reviews" replace />;
  }

  return (
    <div>
      <h1 style={{ fontFamily: "var(--dn-font-display)", color: "var(--dn-green-900)" }}>
        {t.dashboard.title}
      </h1>
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {reviseOk && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {reviseOk}
        </div>
      )}
      <div className="dn-grid">
        <div className="dn-card">
          <h3>{t.dashboard.registration}</h3>
          {reg ? (
            <>
              <p>
                {t.dashboard.registered} ✓ — {reg.registration_code}
              </p>
              <p className="dn-muted">{reg.full_name}</p>
            </>
          ) : (
            <p className="dn-muted">{t.common.loading}</p>
          )}
        </div>
        <div className="dn-card">
          <h3>{t.dashboard.cardAtVenue}</h3>
          <p className="dn-muted">{t.dashboard.cardAtVenueHelp}</p>
        </div>
        <div className="dn-card" id="my-papers">
          <h3>{t.dashboard.papers}</h3>
          {papers.length === 0 ? (
            <p className="dn-muted">{t.dashboard.noPapers}</p>
          ) : (
            <ul>
              {papers.map((p) => (
                <li key={p.id} style={{ marginBottom: "0.75rem" }}>
                  <strong>{p.title}</strong> —{" "}
                  <em>{paperStatusLabel(p.status, p.is_public, t)}</em>
                  {p.current_revision_number && p.current_revision_number > 1 ? (
                    <span className="dn-muted">
                      {" "}
                      · {t.dashboard.revisionLabel.replace("{n}", String(p.current_revision_number))}
                    </span>
                  ) : null}
                  {p.decision || p.decided_at ? (
                    <div
                      style={{
                        marginTop: 6,
                        padding: "0.55rem 0.75rem",
                        background: "rgba(15,79,58,0.08)",
                        borderRadius: 8,
                      }}
                    >
                      <strong>{t.dashboard.editorialDecision}:</strong>{" "}
                      {paperStatusLabel(p.status, p.is_public, t)}
                      {p.decided_at ? (
                        <span className="dn-muted">
                          {" "}
                          · {formatDateTime(p.decided_at, locale)}
                        </span>
                      ) : null}
                      {p.decision_message ? (
                        <p style={{ margin: "0.35rem 0 0" }}>{p.decision_message}</p>
                      ) : null}
                      {canSubmitRevision(p) ? (
                        <div style={{ marginTop: "0.65rem" }}>
                          {reviseId === p.id ? (
                            <form onSubmit={(e) => onSubmitRevision(e, p.id)}>
                              <label style={{ display: "block", marginBottom: 8 }}>
                                {t.dashboard.revisionFile}
                                <input
                                  type="file"
                                  accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                  onChange={(e) => setReviseFile(e.target.files?.[0] || null)}
                                  disabled={reviseBusy}
                                  style={{ display: "block", marginTop: 4 }}
                                  required
                                />
                              </label>
                              <label style={{ display: "block", marginBottom: 8 }}>
                                {t.dashboard.revisionNotes}
                                <textarea
                                  value={reviseNotes}
                                  onChange={(e) => setReviseNotes(e.target.value)}
                                  rows={3}
                                  disabled={reviseBusy}
                                  placeholder={t.dashboard.revisionNotesHelp}
                                  style={{ display: "block", width: "100%", marginTop: 4 }}
                                />
                              </label>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button
                                  type="submit"
                                  className="dn-btn dn-btn-primary"
                                  disabled={reviseBusy}
                                >
                                  {t.dashboard.submitRevision}
                                </button>
                                <button
                                  type="button"
                                  className="dn-btn dn-btn-ghost"
                                  disabled={reviseBusy}
                                  onClick={() => {
                                    setReviseId(null);
                                    setReviseFile(null);
                                    setReviseNotes("");
                                  }}
                                >
                                  {t.common.cancel}
                                </button>
                              </div>
                            </form>
                          ) : (
                            <button
                              type="button"
                              className="dn-btn dn-btn-primary"
                              onClick={() => {
                                setReviseId(p.id);
                                setReviseOk(null);
                                setError(null);
                              }}
                            >
                              {t.dashboard.submitRevisedPaper}
                            </button>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <Link className="dn-btn dn-btn-ghost" to="/submit-paper">
            {t.dashboard.submitPaper}
          </Link>
        </div>
        {reg?.is_reviewer ? (
          <div className="dn-card">
            <h3>{t.dashboard.reviews}</h3>
            <p className="dn-muted">{t.dashboard.reviewsHelp}</p>
            <Link className="dn-btn dn-btn-primary" to="/reviews">
              {t.dashboard.openReviews}
            </Link>
          </div>
        ) : null}
        <div className="dn-card">
          <h3>{t.dashboard.schedule}</h3>
          {nextSession ? (
            <p>
              {pickLocalized(nextSession, locale, "title_en", "title_ar")}
              <br />
              <span className="dn-muted">{formatDateTime(nextSession.starts_at, locale)}</span>
            </p>
          ) : (
            <p className="dn-muted">—</p>
          )}
          <Link className="dn-btn dn-btn-ghost" to="/schedule">
            {t.nav.schedule}
          </Link>
        </div>
        <div className="dn-card">
          <h3>{t.dashboard.liveNow}</h3>
          {live ? (
            <>
              <span className="dn-badge-live">{t.schedule.liveNow}</span>
              <p>{pickLocalized(live, locale, "title_en", "title_ar")}</p>
              <Link className="dn-btn dn-btn-primary" to="/schedule">
                {t.schedule.watchLive}
              </Link>
            </>
          ) : (
            <p className="dn-muted">—</p>
          )}
        </div>
        <div className="dn-card">
          <h3>{t.dashboard.profile}</h3>
          <p>{user?.full_name || user?.email}</p>
          <p className="dn-muted">{user?.email}</p>
        </div>
      </div>
    </div>
  );
}
